import React from 'react'
import { Button, ButtonShape, ButtonStyle } from '../../../components/simple/button'
import { UserSelector } from '../../../components/userSelector/userSelector'
import { makeRequest, showAxiosErrorAsToast, useRequest } from '../../../request'
import { Assessment } from '../assessmentDashboard'
import { Tooltip } from '../../../components/simple/tooltip'
import { BasicUserOrGroup } from '../../../components/userSelector/users'
import { LocalTable } from '../../../components/table'
import { DateWrapper } from '../../../components/simple/date'
import { DonutChart, PieData } from '../../../components/charts/pieChart'
import { TextInput } from '../../../components/form/textInput'
import { IconReload } from '../../../icons/library'

type AssessmentSend = {
  id: number
  name: string
  forStudent: string
  date: string
  resend: boolean
  views: number
  failedAttempts: number
}

export const SendMessageTab: React.FC<{
  assessment: Assessment
}> = ({ assessment }) => {
  const [sendToStudents, setSendToStudents] = React.useState(false)
  const [sendToGuardians, setSendToGuardians] = React.useState(false)
  const [recipients, setRecipients] = React.useState<BasicUserOrGroup[]>([])
  const [loading, setLoading] = React.useState(false)
  const sendDisabledMessage = assessment.processed.length == 0 ? 'Make sure to process your documents first' : undefined
  const [sendInfo, setSendInfo] = React.useState<string | undefined>(undefined)
  const [search, setSearch] = React.useState('')

  const { data, reload: reloadSends } = useRequest<{ sends: AssessmentSend[]; sendStats: PieData }>(
    '/frontend_api/getAssessmentSends',
    React.useMemo(() => ({ assessmentID: assessment.id }), [assessment.id])
  )
  const sends = data?.sends

  return (
    <div className="mt-2 flex min-w-[500px] flex-row gap-16 self-center">
      <div className="flex flex-col gap-2">
        <Tooltip text={sendDisabledMessage}>
          <div className={`${sendDisabledMessage ? 'opacity-50 *:pointer-events-none' : ''} flex flex-col gap-2`}>
            <UserSelector<BasicUserOrGroup>
              selectedUsers={recipients}
              setSelectedUsers={setRecipients}
              searchData={React.useMemo(() => ({ assessmentID: assessment.id }), [assessment.id])}
              loadedUsers={React.useMemo(
                () => [
                  {
                    group: true,
                    groupID: 'all',
                    name: `All ${assessment.processed.length} processed documents`,
                  },
                ],
                []
              )}
              url="/frontend_api/searchAssessmentRecipients"
              userDescriptor="students"
              placeholder="Choose whose documents to send"
            />
            <div className="flex flex-row items-center gap-2">
              Send to:
              <Button
                shape={ButtonShape.roundedMedium}
                buttonStyle={sendToStudents ? ButtonStyle.blueLight : ButtonStyle.light}
                onClick={() => setSendToStudents((s) => !s)}
              >
                Students
              </Button>
              <Button
                shape={ButtonShape.roundedMedium}
                buttonStyle={sendToGuardians ? ButtonStyle.blueLight : ButtonStyle.light}
                onClick={() => setSendToGuardians((s) => !s)}
              >
                Guardians
              </Button>
            </div>
            <Button
              logo
              className="self-start"
              disabled={
                recipients.length === 0 || loading || !!sendDisabledMessage || (!sendToStudents && !sendToGuardians)
              }
              onClick={() => {
                setLoading(true)
                makeRequest<string | undefined>('/frontend_api/sendAssessment', {
                  assessmentID: assessment.id,
                  recipients: recipients.map((r) => (r.group ? r.groupID : r.id)),
                  sendToStudents,
                  sendToGuardians,
                })
                  .then((info) => {
                    setSendInfo(info)
                    setRecipients([])
                    reloadSends()
                  })
                  .catch(showAxiosErrorAsToast)
                  .finally(() => setLoading(false))
              }}
            >
              Send Documents
            </Button>
            {sendInfo && <div className="max-w-[350px] rounded bg-warning p-2">{sendInfo}</div>}
          </div>
        </Tooltip>
        {data && (
          <DonutChart
            className="mt-16 max-w-[350px]"
            title="Send Stats (Per Student)"
            legend={{ side: 'left' }}
            data={data.sendStats}
          />
        )}
      </div>
      <div className="flex flex-col gap-2">
        <div className="flex flex-row items-center justify-between">
          <b className="self-center text-xl">All Past Document Sends</b>
          <TextInput value={search} onChange={setSearch} placeholder="Search" />
        </div>
        {sends && (
          <LocalTable
            exportName={`${assessment.name} Send History`}
            search={search}
            columns={[
              {
                header: 'Name',
                accessorFn: (row) => `${row.name}${row.name == row.forStudent ? '' : ` (${row.forStudent})`}`,
              },
              {
                header: 'Date',
                accessorKey: 'date',
                cell: ({ row }) => (
                  <div className="flex items-center gap-1">
                    <DateWrapper detailed date={row.original.date} />
                    {row.original.resend ? <IconReload tooltip="This was a resend" opacity={0.5} /> : null}
                  </div>
                ),
              },
              {
                header: 'Views',
                accessorKey: 'views',
              },
              {
                header: 'Failed Attempts',
                accessorKey: 'failedAttempts',
              },
              {
                header: 'Resend',
                cell: ({ row }) => <ResendButton send={row.original} reloadSends={reloadSends} />,
              },
            ]}
            data={sends}
            defaultSort={[{ id: 'date', desc: true }]}
            pagination={10}
          />
        )}
      </div>
    </div>
  )
}

const ResendButton: React.FC<{
  send: AssessmentSend
  reloadSends: () => void
}> = ({ send, reloadSends }) => {
  const [loading, setLoading] = React.useState(false)
  return (
    <Button
      logo
      showLoader={loading}
      disabled={loading}
      onClick={() => {
        setLoading(true)
        makeRequest('/frontend_api/resendAssessment', {
          inviteID: send.id,
        })
          .then(reloadSends)
          .catch(showAxiosErrorAsToast)
          .finally(() => setLoading(false))
      }}
    >
      Resend
    </Button>
  )
}
